import React from 'react';

import { useMediaQuery, useTheme } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';

import ActionLibraryInstancePrintTemplate from 'components/_print/_templates/ActionLibraryInstancePrintTemplate/ActionLibraryInstancePrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormButtons from 'components/FormButtons';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import ROLES from 'config/constants/ROLES';
import useIsOwner from 'hooks/useIsOwner/useIsOwner';
import usePermissions from 'hooks/usePermissions';
import action_library_messages from 'messages/action_library_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';

type Props = {
  instance: ActionLibraryInstance;
  onDelete: () => void;
};

const ActionLibraryInstanceActions: React.FC<Props> = ({ instance, onDelete }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { checkIsOwner } = useIsOwner();
  const [isOrgAssetAdmin] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const isOwner = checkIsOwner(instance.owner);
  const canDelete = isOrgAssetAdmin || isOwner;

  const { mutate: deleteInstance, isLoading: deleteLoading } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
      enqueueSnackbar(t(action_library_messages.success.instance_deleted), { variant: 'success' });
      onDelete();
    },
    onError: () => {
      enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
    },
  });

  const handleEdit = () => {
    history.push(generatePath(PATHS.ACTION_LIBRARY_INSTANCE_EDIT, { id: instance.id }));
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmationModal({
      title: t(action_library_messages.delete_confirmation.title),
      body: t(action_library_messages.delete_confirmation.body),
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  return (
    <CenteredGrid width='lg' withoutPadding>
      <FormButtons inline={!isMobile}>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={deleteLoading}
          icon={false}
          onClick={handleEdit}
          showLoader={false}
          startIcon={<EditOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.edit)}
        </ColoredButton>
        <PrintComponent
          ctaRenderer={({ onPrint }) => (
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={deleteLoading}
              icon={false}
              onClick={onPrint}
              showLoader={false}
              startIcon={<PrintOutlinedIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.actions.print)}
            </ColoredButton>
          )}
          templateComponent={<ActionLibraryInstancePrintTemplate instance={instance} />}
        />
        {canDelete && (
          <ColoredButton
            className=''
            customColor='danger'
            disabled={deleteLoading}
            icon={false}
            onClick={handleDelete}
            showLoader={deleteLoading}
            startIcon={<DeleteOutlinedIcon />}
            textLike={false}
            variant='outlined'
          >
            {t(action_library_messages.actions.delete)}
          </ColoredButton>
        )}
      </FormButtons>
    </CenteredGrid>
  );
};

export default ActionLibraryInstanceActions;
